import {
  Box,
  Collapse,
  Portal,
  SimpleGrid,
  Text,
  Tooltip,
  useBreakpointValue
} from '@chakra-ui/react'
import {
  Bot,
  Brain,
  Briefcase,
  Code,
  Coffee,
  Database,
  FileText,
  Globe,
  Heart,
  Image,
  Mail,
  MessageSquare,
  Music,
  Pencil,
  Search,
  Settings,
  Share2,
  ShoppingBag,
  Smile,
  Terminal,
  User,
  Video,
  Zap
} from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { useLanguage } from '../../../hooks/utility/useLanguage'

export const availableIcons = [
  { icon: Bot, name: 'Bot' },
  { icon: Brain, name: 'Brain' },
  { icon: Briefcase, name: 'Briefcase' },
  { icon: Code, name: 'Code' },
  { icon: Coffee, name: 'Coffee' },
  { icon: Database, name: 'Database' },
  { icon: FileText, name: 'Document' },
  { icon: Globe, name: 'Globe' },
  { icon: Heart, name: 'Heart' },
  { icon: Image, name: 'Image' },
  { icon: Mail, name: 'Mail' },
  { icon: MessageSquare, name: 'Message' },
  { icon: Music, name: 'Music' },
  { icon: Pencil, name: 'Pencil' },
  { icon: Search, name: 'Search' },
  { icon: Settings, name: 'Settings' },
  { icon: Share2, name: 'Share' },
  { icon: ShoppingBag, name: 'Shopping' },
  { icon: Smile, name: 'Smile' },
  { icon: Terminal, name: 'Terminal' },
  { icon: User, name: 'User' },
  { icon: Video, name: 'Video' },
  { icon: Zap, name: 'Zap' },
]

interface IconSelectorProps {
  selectedIcon: string
  onIconSelect: (iconName: string) => void
}

export const IconSelector: React.FC<IconSelectorProps> = ({
  selectedIcon,
  onIconSelect,
}) => {
  const { t } = useLanguage()
  const [showIconGrid, setShowIconGrid] = useState(false)
  const iconGridCols = useBreakpointValue({ base: 4, sm: 6, md: 8, lg: 10 })
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [popupStyle, setPopupStyle] = useState({})

  useEffect(() => {
    if (triggerRef.current && showIconGrid) {
      const rect = triggerRef.current.getBoundingClientRect()
      setPopupStyle({
        position: 'absolute',
        left: `${rect.left + window.scrollX}px`,
        top: `${rect.bottom + window.scrollY + 8}px`, // 8px gap
        zIndex: 1501,
      })
    }
  }, [showIconGrid])

  const selectedIconComponent = availableIcons.find((icon) => icon.name === selectedIcon)?.icon || Bot

  const handleIconSelect = (iconName: string) => {
    onIconSelect(iconName)
    setShowIconGrid(false)
  }

  return (
    <Box>
      {/* Current Selection Display */}
      <Tooltip label={selectedIcon} placement='top'>
        <Box
          ref={triggerRef}
          as='button'
          type='button'
          onClick={() => setShowIconGrid(!showIconGrid)}
          p={3}
          borderRadius='md'
          borderWidth='1px'
          borderColor='app.border'
          bg='app.bg'
          display='flex'
          alignItems='center'
          justifyContent='center'
          width='48px'
          height='48px'
          _hover={{
            borderColor: 'primaryScheme.500',
            bg: 'primaryScheme.50',
            transition: 'all 0.2s',
          }}
          _focus={{
            outline: 'none',
            borderColor: 'primaryScheme.500',
            boxShadow: '0 0 0 1px var(--chakra-colors-primaryScheme-500)',
          }}
        >
          {React.createElement(selectedIconComponent, { size: 20 })}
        </Box>
      </Tooltip>

      {/* Icon Grid Overlay */}
      <Portal>
      <Collapse in={showIconGrid} animateOpacity>
        <Box
          {...popupStyle}
          p={4}
          borderRadius='lg'
          borderWidth='1px'
          borderColor='app.border'
          bg='app.bg'
          boxShadow='lg'
          minWidth='320px'
          maxWidth='400px'
        >
          <Text fontSize='sm' color='app.text.muted' mb={3}>
            {t('iconSelector.chooseIcon')}
          </Text>
          <SimpleGrid columns={iconGridCols} spacing={2}>
            {availableIcons.map(({ icon: IconComponent, name }) => (
              <Tooltip key={name} label={name} placement='top'>
                <Box
                  as='button'
                  type='button'
                  onClick={() => handleIconSelect(name)}
                  p={2}
                  borderRadius='md'
                  borderWidth='1px'
                  borderColor={selectedIcon === name ? 'primaryScheme.500' : 'app.border'}
                  bg={selectedIcon === name ? 'primaryScheme.50' : 'app.bg'}
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                  width='32px'
                  height='32px'
                  _hover={{
                    borderColor: 'primaryScheme.500',
                    bg: 'primaryScheme.50',
                    transition: 'all 0.2s',
                  }}
                  _focus={{
                    outline: 'none',
                    borderColor: 'primaryScheme.500',
                    boxShadow: '0 0 0 1px var(--chakra-colors-primaryScheme-500)',
                  }}
                >
                  <IconComponent size={16} />
                </Box>
              </Tooltip>
            ))}
          </SimpleGrid>
          </Box>
        </Collapse>
      </Portal>
    </Box>
  )
}

export { availableIcons as iconOptions }
